import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/* ------------------------------------------------------------------ */
/* Store code — the chain's own number for this account                */
/* ------------------------------------------------------------------ */

export function useSetStoreCode(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storeCode: string | null) =>
      adminFetch<{ ok: true; store_code: string | null }>('admin-set-store-code', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, store_code: storeCode }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'customer-detail', customerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Store contacts — people, not logins                                 */
/* ------------------------------------------------------------------ */

export type ContactRole =
  | 'gm'
  | 'district_manager'
  | 'vp_ops'
  | 'facilities'
  | 'ap'
  | 'ordering'
  | 'receiving'
  | 'other';

export interface CustomerContact {
  id: string;
  customer_id: string;
  name: string;
  role: ContactRole;
  title: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  source: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export const CONTACT_ROLE_LABELS: Record<ContactRole, string> = {
  gm: 'General manager',
  district_manager: 'District manager',
  vp_ops: 'VP operations',
  facilities: 'Facilities',
  ap: 'Accounts payable',
  ordering: 'Ordering',
  receiving: 'Receiving',
  other: 'Other',
};

export function useCustomerContacts(customerId: string, includeInactive = false) {
  return useQuery({
    queryKey: ['admin', 'customer-contacts', customerId, includeInactive],
    queryFn: () =>
      adminFetch<{ ok: true; contacts: CustomerContact[] }>(
        `admin-customer-contacts?customer_id=${encodeURIComponent(customerId)}` +
          (includeInactive ? '&inactive=1' : ''),
      ),
    staleTime: 30_000,
  });
}

export interface ContactInput {
  name?: string;
  role?: ContactRole;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

export function useSaveContact(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContactInput & { contact_id?: string }) =>
      adminFetch<{ ok: true; contact: CustomerContact }>('admin-customer-contacts', {
        method: 'POST',
        body: JSON.stringify({
          customer_id: customerId,
          action: input.contact_id ? 'update' : 'create',
          ...input,
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'customer-contacts', customerId] });
    },
  });
}

export function useSetContactActive(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { contact_id: string; active: boolean }) =>
      adminFetch<{ ok: true; contact: CustomerContact }>('admin-customer-contacts', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, action: 'set_active', ...input }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'customer-contacts', customerId] });
    },
  });
}
